/* eslint-disable no-shadow, no-param-reassign */
import axios from "axios";
import swal from "sweetalert2";

const localStorageExpiration =
  1000 * // 1000 ms = 1s
  60 * // 60s = 1min
  60; // 60min = 1hr

// initial state
const state = () => ({
  locale: null,
  regions: [],
  stripeKey: null,
  stripeAPIs: {},
  userId: null,
  userAchAccess: null,
  userStripePaymentIntentAccess: null,
  userAchCreditAccess: null,
  acceptNetTerms: false,
  netTermsPayments: false,
  hasUserAchCredit: false,
  hasUserBankTransfer: false,
  purchaseOrder: null,
  shippingAddressId: null,
  billingAddressId: null,
  paymentMethodId: null,
  estimatedTax: undefined,
  cartItems: [],
  testDrives: null,
  addresses: [],
  paymentMethods: [],
  addingAddress: false,
  processing: false,
  hasUpgradeableTestdrive: false,
  hasUpgradeableNoImplementationFee: false,
  components: {
    shipping: {
      loading: false,
      editing: false,
      error: false,
    },
    billing: {
      loading: false,
      editing: false,
      error: false,
    },
    payment: {
      loading: false,
      editing: false,
      error: false,
    },
    items: {
      loading: false,
      editing: false,
      error: false,
    },
    submit: {
      loading: false,
      editing: false,
      error: false,
    },
  },
});

// getters
const getters = {
  canCheckout: (state, getters) => {
    return (
      state.shippingAddressId !== null &&
      state.billingAddressId !== null &&
      (state.paymentMethodId !== null || state.netTermsPayments) &&
      !getters.cartIsEmpty &&
      !getters.hasComponentEditing &&
      !getters.hasComponentLoading &&
      getters.allTermsAccepted
    );
  },

  canGetTax: (state, getters) => {
    return (
      state.shippingAddressId !== null &&
      state.billingAddressId !== null &&
      !getters.cartIsEmpty &&
      !getters.hasComponentEditing &&
      !getters.hasComponentLoading
    );
  },

  checkoutSubmitError: (state, getters) => {
    if (state.shippingAddressId === null) {
      return "A shipping address must be selected.";
    }
    if (state.billingAddressId === null) {
      return "A billing address must be selected.";
    }
    if (state.paymentMethodId === null && !state.netTermsPayments) {
      return "A payment method must be selected.";
    }
    if (getters.cartIsEmpty) {
      return "Your cart is empty!";
    }
    if (!getters.allTermsAccepted) {
      return "All item terms must be accepted!";
    }

    return "";
  },

  country: (state) => {
    return state.locale.locale.substring(3);
  },

  regions: (state) => {
    return state.regions;
  },

  hasUpgradeableTestdrive: (state) => {
    return state.hasUpgradeableTestdrive;
  },

  hasUpgradeableNoImplementationFee: (state) => {
    return state.hasUpgradeableNoImplementationFee;
  },

  processing: (state) => {
    return state.processing;
  },

  currency: (state) => {
    return state.locale.currency;
  },

  stripeKey: (state) => {
    return state.stripeKey;
  },

  stripeAPIsConf: (state) => {
    return state.stripeAPIs;
  },

  userId: (state) => {
    return state.userId;
  },

  userAchAccess: (state) => {
    return state.userAchAccess;
  },

  acceptNetTerms: (state) => {
    return state.acceptNetTerms;
  },

  netTermsPayments: (state) => {
    return state.netTermsPayments;
  },

  userAchCreditAccess: (state) => {
    return state.userAchCreditAccess;
  },

  userStripePaymentIntentAccess: (state) => {
    return state.userStripePaymentIntentAccess;
  },

  hasUserAchCredit: (state) => {
    return state.hasUserAchCredit;
  },

  hasUserBankTransfer: (state) => {
    return state.hasUserBankTransfer;
  },

  cartIsEmpty: (state) => {
    return state.cartItems.length < 1;
  },

  addingAddress: (state) => {
    return state.addingAddress;
  },

  componentState: (state) => {
    return state.components;
  },

  addresses: (state) => {
    return state.addresses;
  },

  shippingAddress: (state) => {
    return state.addresses.find(
      (address) => address.id === state.shippingAddressId
    );
  },

  billingAddress: (state) => {
    return state.addresses.find(
      (address) => address.id === state.billingAddressId
    );
  },

  paymentMethods: (state) => {
    return state.paymentMethods;
  },

  paymentMethod: (state) => {
    return !state.netTermsPayments
      ? state.paymentMethods.find(
          (payment) => payment.id === state.paymentMethodId
        )
      : null;
  },

  cartItems: (state) => {
    return state.cartItems;
  },

  estimatedTax: (state) => {
    return state.estimatedTax;
  },

  purchaseOrder: (state) => {
    return state.purchaseOrder;
  },

  allTermsAccepted: (state) => {
    return !state.cartItems.some((cartItem) => cartItem.terms === false);
  },

  hasComponentEditing: (state) => {
    return Object.values(state.components).some(
      (component) => component.editing === true
    );
  },

  hasComponentLoading: (state) => {
    return Object.values(state.components).some(
      (component) => component.loading === true
    );
  },

  /**
   * Returns a specific item's related metadata value for the input key
   */
  itemMeta: (state) => (itemId, key) => {
    const index = state.cartItems.findIndex(
      (cartItem) => cartItem.item.id === itemId
    );
    // eslint-disable-next-line camelcase
    const { item_meta } = state.cartItems[index].item;

    return item_meta.find((meta) => meta.item_meta === key).item_value;
  },

  /**
   * Returns an item's cycle price from related ItemMeta, which is currently
   * the accurate price of any item for any cycle.
   * ~~~
   * Notice that the meta value being returned is cast to Number type, as
   * otherwise this will return a string containing a number which would lead to
   * concatenation of values instead of arithmetic operations
   */
  productCost: (state, getters) => (cartItemId) => {
    const itemId = state.cartItems.find(
      (cartItem) => cartItem.id === cartItemId
    ).item.id;

    return Number(getters.itemMeta(itemId, "cycle_price"));
  },

  /**
   * Returns a `Number` value representing the sum of the cost of a specific
   * item's addon(s).
   * ~~~
   * This method makes some assumptions about product schema, most notably the
   * fact that although all current products only contain at-most one (1) addon,
   * the relation to items exists either an empty array, or an array containing
   * only one (1) addon. This also assumes that if an addon exists, that it is
   * always applied to the first subscription cycle.
   */
  addonCost: (state) => (cartItemId) => {
    const index = state.cartItems.findIndex(
      (cartItem) => cartItem.id === cartItemId
    );

    const addons = state.cartItems[index].item.add_on;

    if (addons.length === 0) return 0;
    return addons
      .map((addon) => Number(addon.amount))
      .reduce((a, b) => {
        return a + b;
      }, 0);
  },

  /**
   * Returns a `Number` value representing the sum of the value of a specific
   * item's discount(s).
   * ~~~
   * This method makes some assumptions about product schema, most notably the
   * fact that any discount will always apply to the first cycle of any given
   * product subscription.
   */
  discountCost: (state) => (cartItemId) => {
    const index = state.cartItems.findIndex(
      (cartItem) => cartItem.id === cartItemId
    );

    const { discounts } = state.cartItems[index].item;

    if (discounts.length === 0) return 0;
    return discounts
      .map((discount) => Number(discount.amount))
      .reduce((a, b) => {
        return a + b;
      }, 0);
  },

  /**
   * Returns a `Number` value representing the total first-cycle cost of an item
   * considering any addons or discounts.
   */
  cartItemPrice: (state, getters) => (cartItemId) => {
    const productCost = getters.productCost(cartItemId);
    const addonCost = getters.addonCost(cartItemId);
    const discountCost = getters.discountCost(cartItemId);
    return productCost + addonCost - discountCost;
  },

  /**
   * Returns the aggregate cost of the product of each shopping cart item by the
   * item's quantity
   */
  cartSubTotal: (state, getters) => {
    return state.cartItems.reduce((total, cartItem) => {
      return total + cartItem.quantity * getters.cartItemPrice(cartItem.id);
    }, 0);
  },

  estimatedGrandTotal: (state, getters) => {
    let total = getters.cartSubTotal;
    if (state.estimatedTax) total += state.estimatedTax;
    return total;
  },

  /**
   * Returns the total quantity of items in the user's shopping cart
   */
  cartItemsCount: (state) => {
    return state.cartItems
      .map((cartItem) => cartItem.quantity)
      .reduce((a, b) => {
        return a + b;
      }, 0);
  },
};

// actions
const actions = {
  /**
   * Used to asynchronously update an item in the user's shopping cart. Receives
   * the id of the ShoppingCartItem (not the Item) and the desired quantity. A
   * POST is made to the related application route, and the resulting response
   * contains ALL current items in the user's cart, which is used to update the
   * state object.
   */
  updateItemQuantity({ commit, state, dispatch }, { cartItemId, quantity }) {
    commit("SET_LOADING", { componentName: "items", boolVal: true });

    axios
      .post(`/users/${state.userId}/cart`, { cartItemId, quantity })
      .then((res) => {
        if (res.data.errors) {
          swal.fire({
            title: "Uh oh..",
            type: "error",
            html: `<p style="font-size: small">We were unable to update your cart!</p>
            <p style="font-size: small">${res.data.errors}</p>
            <p style="font-size: small">Please refresh your page and try again.</p>
          `,
          });
          return;
        }
        const items = res.data.map((shoppingCartItem) => {
          return {
            id: shoppingCartItem.id,
            item: shoppingCartItem.item,
            quantity: shoppingCartItem.quantity,
            terms: false,
          };
        });

        commit("SET_CART_ITEMS", items);
        commit("SET_ESTIMATED_TAX", undefined);
        // check if we need to show the upgrade testdrive popup
        dispatch("setUpgradeableTestdrive");
        dispatch("setUpgradeableNoImplementationFee", res.data);
      })
      .catch((err) => {
        swal.fire({
          title: "Uh oh..",
          type: "error",
          html: `<p style="font-size: small">We were unable to update your cart!</p>
            <p style="font-size: small">Please refresh your page and try again.</p>
          `,
        });
        commit("SET_ERROR", { componentName: "items", message: err.message });
      })
      .finally(() => {
        commit("SET_LOADING", { componentName: "items", boolVal: false });
      });
  },

  /**
   * Used to create a Card object tied to the current user from a Stripe Source
   * object returned from StripeJS call using Stripe Elements. Response returned
   * from application route will contain the single newly created Card object,
   * which is added to the state's paymentMethods array. The user's selected
   * payment method is also set to the newly created card.
   */
  createCardFromNewPaymentElement({ commit, state }, paymentElement) {
    commit("SET_LOADING", { componentName: "payment", boolVal: true });

    axios
      .post(`/users/${state.userId}/cards`, {
        paymentElement,
        addressId: state.billingAddressId,
      })
      .then((res) => {
        const paymentMethod = res.data;
        const updatedPaymentMethods = state.paymentMethods;
        updatedPaymentMethods.push(paymentMethod);
        commit("SET_PAYMENT_METHOD_ID", paymentMethod.id);
        commit("SET_PAYMENT_METHODS", updatedPaymentMethods);
        commit("SET_EDITING", {
          componentName: "payment",
          boolVal: false,
        });
      })
      .catch((err) => {
        let message = err.response.data ? err.response.data : err;
        if (typeof message !== "string") {
          message = message.message
            ? message.message
            : "Invalid or missing data.";
        }
        swal.fire({
          title: "Uh oh..",
          type: "error",
          html: `<p style="font-size: small">We are unable to verify your payment method!</p>
            <p style="font-size: small; margin-bottom: 0.5rem">The following response was received:</p>
            <b>${message}</b>
          `,
        });
      })
      .finally(() => {
        commit("SET_LOADING", { componentName: "payment", boolVal: false });
      });
  },

  estimateTax({ commit, state }) {
    axios
      .post("/checkout/estimateTax", { id: state.shippingAddressId })
      .then((res) => {
        commit("SET_ESTIMATED_TAX", res.data);
      })
      .catch((e) => {
        swal.fire({
          title: "Uh oh...",
          type: "error",
          html: `<p>${e}</p>`,
        });
      });
  },

  submit({ commit, state }) {
    commit("SET_PROCESSING", true);

    const cartItems = state.cartItems.map((cartItem) => {
      return {
        itemId: cartItem.item_id,
        quantity: cartItem.quantity,
      };
    });

    axios
      .post("/checkout/process", {
        shippingId: state.shippingAddressId,
        netTerms: state.netTermsPayments,
        billingId: state.billingAddressId,
        paymentMethodId: state.netTermsPayments ? null : state.paymentMethodId,
        purchaseOrder: state.purchaseOrder,
        cartItems,
      })
      .then((res) => {
        window.console.log(res);
        // window.localStorage.removeItem("checkoutOptions");
        window.location.href = res.request.responseURL;
      })
      .catch((e) => {
        if (e.response) {
          let errors = "";
          if (e.response.data.errors) {
            errors = Object.values(e.response.data.errors)
              .map((err) => `<p style="margin: 0; color: red;">${err}</p>`)
              .join("");
          }

          swal.fire({
            title: "Uh oh...",
            type: "error",
            html: `<p style="font-size: small">
              ${e.response.data.message}</p>${errors}`,
          });
        } else if (e.request) {
          swal.fire({
            title: "Uh oh...",
            type: "error",
            html: `<p style="font-size: small">We're having some difficulty
              completing your request. Please refresh your browser and try
              again.</p><p style="font-size: small">If this problem persists,
              please contact support.</p>`,
          });
        } else {
          swal.fire({
            title: "Uh oh...",
            type: "error",
            html: `<p style="font-size: small">Something went wrong. Please
              refresh your browser and try again.</p>`,
          });
        }

        commit("SET_PROCESSING", false);
      });
  },

  updateHasUserAchCredit({ commit }, value) {
    commit("SET_HAS_ACH_CREDIT", value);
  },

  updateHasUserBankTransfer({ commit }, value) {
    commit("SET_HAS_BANK_TRANSFER", value);
  },

  setUpgradeableTestdrive({ state, commit }) {
    // Before iterate the CartItems we assume upgradeable is false
    commit("SET_HAS_UPGRADEABLE_TESTDRIVE", false);
    const testDrivesInCart = state.testDrives.filter((td) => td.in_cart !== 0);
    const testDrivesNotInCart = state.testDrives.filter(
      (td) => td.in_cart === 0
    );

    // Iterate the cartItems
    state.cartItems.every((cart) => {
      // Get total of testdrives quantity "NOT upgraded" with same bundle_item_id
      const testDriveNotUpgradedQty = testDrivesNotInCart.reduce(
        (total, td) => {
          return total + (td.item_bundle_id === cart.item.id ? 1 : 0);
        },
        0
      );

      // Get total quantity of testdrives "upgraded already" with same bundle_item_id
      const testDriveUpgradedQty = testDrivesInCart.reduce((total, td) => {
        return total + (td.item_bundle_id === cart.item.id ? 1 : 0);
      }, 0);

      // If we have from any cartItem more quantity than testdrives upgraded and
      // We have more testdrives using the same item not upgraded then
      // show the popup
      if (cart.quantity > testDriveUpgradedQty && testDriveNotUpgradedQty > 0) {
        commit("SET_HAS_UPGRADEABLE_TESTDRIVE", true);
        // break loop
        return false;
      }
      // keep looping
      return true;
    });
  },

  setUpgradeableNoImplementationFee({ commit }, cartItems) {
    commit("SET_HAS_UPGRADEABLE_NO_IMPLEMENTATION_FEE", false);
    const upgradeItems = cartItems.filter((item) => {
      return !!item.upgrade_order_item_id;
    });
    if (upgradeItems.length > 0) {
      commit("SET_HAS_UPGRADEABLE_NO_IMPLEMENTATION_FEE", true);
    }
  },
};

// mutations
const mutations = {
  SET_LOCALE(state, locale) {
    state.locale = locale;
  },
  SET_REGIONS(state, regions) {
    state.regions = regions;
  },
  SET_STRIPE_KEY(state, key) {
    state.stripeKey = key;
  },
  SET_STRIPE_APIS(state, key) {
    state.stripeAPIs = key;
  },
  SET_USER_ID(state, userId) {
    state.userId = userId;
  },
  SET_ACH_ACCESS(state, hasAccess) {
    state.userAchAccess = hasAccess;
  },
  SET_ACCEPT_NET_TERMS(state, acceptNetTerms) {
    state.acceptNetTerms = acceptNetTerms;
  },
  SET_NET_TERMS_PAYMENTS(state, netTermsPayments) {
    state.netTermsPayments = netTermsPayments;
  },
  SET_ACH_CREDIT_ACCESS(state, hasAccess) {
    state.userAchCreditAccess = hasAccess;
  },
  SET_STRIPE_PAYMENT_INTENT_ACCESS(state, hasAccess) {
    state.userStripePaymentIntentAccess = hasAccess;
  },
  SET_ADDRESSES(state, addresses) {
    state.addresses = addresses;
  },
  SET_PAYMENT_METHODS(state, paymentMethods) {
    state.paymentMethods = paymentMethods;
  },
  SET_CART_ITEMS(state, cartItems) {
    state.cartItems = cartItems;
  },
  SET_TESTDRIVES(state, testDrives) {
    state.testDrives = testDrives;
  },
  SET_HAS_UPGRADEABLE_TESTDRIVE(state, hasUpgradeableTestdrive) {
    state.hasUpgradeableTestdrive = hasUpgradeableTestdrive;
  },
  SET_HAS_UPGRADEABLE_NO_IMPLEMENTATION_FEE(
    state,
    hasUpgradeableNoImplementationFee
  ) {
    state.hasUpgradeableNoImplementationFee = hasUpgradeableNoImplementationFee;
  },
  SET_SHIPPING_ADDRESS_ID(state, shippingAddressId) {
    state.shippingAddressId = shippingAddressId;

    const options = JSON.parse(window.localStorage.getItem("checkoutOptions"));

    window.localStorage.setItem(
      "checkoutOptions",
      JSON.stringify({
        ...options,
        sId: shippingAddressId,
        expiresAt: Date.now() + localStorageExpiration,
      })
    );
  },
  SET_BILLING_ADDRESS_ID(state, billingAddressId) {
    state.billingAddressId = billingAddressId;

    const options = JSON.parse(window.localStorage.getItem("checkoutOptions"));

    window.localStorage.setItem(
      "checkoutOptions",
      JSON.stringify({
        ...options,
        bId: billingAddressId,
        expiresAt: Date.now() + localStorageExpiration,
      })
    );
  },
  SET_PAYMENT_METHOD_ID(state, paymentMethodId) {
    state.paymentMethodId = paymentMethodId;

    const options = JSON.parse(window.localStorage.getItem("checkoutOptions"));

    window.localStorage.setItem(
      "checkoutOptions",
      JSON.stringify({
        ...options,
        pId: paymentMethodId,
        expiresAt: Date.now() + localStorageExpiration,
      })
    );
  },
  SET_CART_ITEM_TERMS(state, { cartItemId, boolVal }) {
    const index = state.cartItems.findIndex((item) => item.id === cartItemId);
    state.cartItems[index].terms = boolVal;
  },
  SET_NEW_ADDRESS(state, addressObj) {
    state.addresses.push(addressObj);
  },
  SET_LOADING(state, { componentName, boolVal }) {
    state.components[componentName].loading = boolVal;
  },
  SET_EDITING(state, { componentName, boolVal }) {
    Object.values(state.components).forEach((component) => {
      component.editing = false;
    });

    state.components[componentName].editing = boolVal;
  },
  SET_ERROR(state, { componentName, message }) {
    state.components[componentName].error = message;
  },
  SET_PROCESSING(state, value) {
    state.processing = value;
  },
  SET_ESTIMATED_TAX(state, value) {
    state.estimatedTax = value;
  },
  SET_PURCHASE_ORDER(state, value) {
    state.purchaseOrder = value;
  },
  SET_HAS_ACH_CREDIT(state, value) {
    state.hasUserAchCredit = value;
  },
  SET_HAS_BANK_TRANSFER(state, value) {
    state.hasUserBankTransfer = value;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
